import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { API_GET_ENQUIRY_DATA } from "../../Util/URLConstants";
import { updateColorBasedOnStatus } from "../../Util/UtilHelper";
import { VM_Colors } from "../../Styles/VMStyles";
import StatusButton from "../../Components/StatusButton";
import CreatedByCell from "../../Components/Custom/CreatedByCell";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import {
  formatDisplayDateTime,
  getEvents,
  getEventPhotos,
  getUserById,
  getVenueBilling,
  getVenueById,
  getVendors,
  getVisitorLogs,
} from "../../api/superAdminApi";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminEnquiryDetails from "./SuperAdminEnquiryDetails";

const PAGE_SIZE = 10;

const ENQUIRY_FILTERS = ["All", "Lead", "Confirmed", "Not-interested"];

const SECTIONS = [
  {
    key: "events",
    title: "Events / bookings",
    fetch: getEvents,
    columns: [
      { label: "Event", value: (row) => row.event_name },
      { label: "Type", value: (row) => row.event_type },
      { label: "Customer", value: (row) => row.customer_name },
      { label: "Event date", value: (row) => formatDisplayDateTime(row.event_date) },
      { label: "Guests", value: (row) => row.event_no_of_guests },
      { label: "Enquiry status", value: (row) => row.enquiry_status },
    ],
  },
  {
    key: "visitors",
    title: "Visitor logs",
    fetch: getVisitorLogs,
    columns: [
      { label: "Name", value: (row) => row.name },
      { label: "Type", value: (row) => row.type },
      { label: "Phone", value: (row) => row.phone_no },
      { label: "Check in", value: (row) => formatDisplayDateTime(row.check_in) },
      { label: "Check out", value: (row) => formatDisplayDateTime(row.check_out) },
    ],
  },
  {
    key: "vendors",
    title: "Event vendors",
    fetch: getVendors,
    columns: [
      { label: "Vendor", value: (row) => row.vendor_name },
      { label: "Type", value: (row) => row.vendor_type },
      { label: "Company", value: (row) => row.vendor_company },
      { label: "Phone", value: (row) => row.phone_no },
      { label: "Created", value: (row) => formatDisplayDateTime(row.created_at) },
    ],
  },
  {
    key: "photos",
    title: "Event photos",
    fetch: getEventPhotos,
    columns: [
      { label: "Enquiry", value: (row) => row.enquiry_id },
      { label: "Customer", value: (row) => row.customer_name },
      { label: "Photos", value: (row) => row.photo_count },
      { label: "Updated", value: (row) => formatDisplayDateTime(row.updated_date) },
    ],
  },
];

function emptySections() {
  return SECTIONS.reduce((acc, section) => {
    acc[section.key] = { rows: [], total: 0, page: 0, loading: false };
    return acc;
  }, {});
}

function emptyBilling() {
  const pageData = { data: [], totalCount: 0, page: 0, no_of_rows: PAGE_SIZE };
  return {
    subscriptions: { ...pageData },
    invoices: { ...pageData },
    payments: { ...pageData },
    invoice_summary: { total: 0, invoiced_amount: 0, paid_amount: 0, pending_amount: 0 },
  };
}

function listPayload(res) {
  const body = res?.data ?? {};
  const rows = Array.isArray(body.data) ? body.data : [];
  return { rows, total: Number(body.totalCount ?? body.total ?? rows.length) };
}

function formatSubVenueInfo(enq) {
  const names = Array.isArray(enq?.event_sub_venue_names)
    ? enq.event_sub_venue_names.filter(Boolean)
    : [];
  const types = Array.isArray(enq?.event_sub_venue_types)
    ? enq.event_sub_venue_types.filter(Boolean)
    : [];
  if (names.length) {
    return names
      .map((name, index) => (types[index] ? `${name} (${types[index]})` : name))
      .join(", ");
  }
  const fromObjects = Array.isArray(enq?.event_sub_venues)
    ? enq.event_sub_venues
        .map((venue) => {
          if (!venue) return "";
          if (typeof venue === "string") return venue;
          const name = venue.sub_venue_name || "";
          const type = venue.sub_venue_type || "";
          if (name && type) return `${name} (${type})`;
          return name || type;
        })
        .filter(Boolean)
    : [];
  return fromObjects.join(", ");
}

function show(value) {
  if (value == null || value === "") return "—";
  return value;
}

function Field({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{show(value)}</Typography>
    </Grid>
  );
}

function subscriptionLabel(venue) {
  if (!venue?.has_subscription) return "None";
  return venue.is_subscription_active ? "Active paid plan" : "Has subscription (not active)";
}

function formatAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : show(value);
}

function BillingTable({ title, rows, columns, count, page, onPageChange, loading }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {columns.map((column) => (
                <TableCell key={column.label} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No {title.toLowerCase()} for this venue.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.id ?? row.invoice_id ?? index} hover>
                  {columns.map((column) => (
                    <TableCell key={column.label}>{show(column.value(row))}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[PAGE_SIZE]}
          count={count}
          rowsPerPage={PAGE_SIZE}
          page={page}
          onPageChange={onPageChange}
        />
      </TableContainer>
    </Box>
  );
}

function SubscriptionHistoryTimeline({ summary, events, page, loading, onPageChange }) {
  const active = summary?.current_subscription;
  const items = events?.data || [];
  const eventTitle = (eventType) =>
    String(eventType || "Subscription update")
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  const displayAmount = (event) => {
    if (event.amount == null) return null;
    return `${event.currency || "INR"} ${formatAmount(event.amount)}`;
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Current subscription summary
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Field label="Plan" value={active?.plan_name || active?.plan_tier || "Free / Trial"} />
          <Field label="Status" value={summary?.status} />
          <Field label="Started" value={formatDisplayDateTime(summary?.started_at)} />
          <Field label="Next billing / end date" value={formatDisplayDateTime(summary?.next_billing_date)} />
          <Field label="AutoPay" value={summary?.autopay_status} />
          <Field label="Total paid" value={`${active?.currency || "INR"} ${formatAmount(summary?.total_paid)}`} />
          <Field label="Successful payments" value={summary?.successful_payments ?? 0} />
          <Field label="Failed payments" value={summary?.failed_payments ?? 0} />
          <Field label="Refunds" value={summary?.refunds ?? 0} />
          <Field label="Razorpay subscription ID" value={active?.razorpay_subscription_id} />
        </Grid>
      </Paper>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Lifecycle timeline
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
          No subscription lifecycle events are available.
        </Typography>
      ) : (
        <Box sx={{ pl: 1 }}>
          {items.map((event, index) => (
            <Box
              key={event.id || `${event.event_type}-${event.created_at}-${index}`}
              sx={{
                display: "grid",
                gridTemplateColumns: "22px minmax(0, 1fr)",
                columnGap: 1.5,
                pb: 2,
              }}
            >
              <Box sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
                {index < items.length - 1 && (
                  <Box sx={{ position: "absolute", top: 12, bottom: -16, width: "2px", bgcolor: "divider" }} />
                )}
                <Box sx={{ zIndex: 1, mt: 0.5, width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main" }} />
              </Box>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {eventTitle(event.event_type)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {formatDisplayDateTime(event.created_at)}
                </Typography>
                {event.description && (
                  <Typography variant="body2" sx={{ mb: 1 }}>{event.description}</Typography>
                )}
                <Grid container spacing={1}>
                  {[
                    ["Plan", event.plan_name],
                    ["Amount", displayAmount(event)],
                    ["Payment", event.payment_status],
                    ["Subscription ID", event.subscription_id],
                    ["Razorpay subscription ID", event.razorpay_subscription_id],
                    ["Razorpay order ID", event.razorpay_order_id],
                    ["Razorpay payment ID", event.razorpay_payment_id],
                    ["Previous status", event.previous_status],
                    ["New status", event.new_status],
                    [
                      "Billing period",
                      event.billing_period_start || event.billing_period_end
                        ? `${formatDisplayDateTime(event.billing_period_start)} – ${formatDisplayDateTime(event.billing_period_end)}`
                        : null,
                    ],
                    [
                      "Next billing date",
                      event.next_billing_date ? formatDisplayDateTime(event.next_billing_date) : null,
                    ],
                    ["AutoPay", event.autopay_status],
                    ["Created by", event.created_by],
                    ["IP address", event.ip_address],
                    ["Device / browser", event.user_agent],
                    ["Failure reason", event.failure_reason],
                  ]
                    .filter(([, value]) => value != null && value !== "")
                    .map(([label, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {label}
                        </Typography>
                        <Typography variant="body2">{value}</Typography>
                      </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>
          ))}
        </Box>
      )}
      <TablePagination
        component="div"
        rowsPerPageOptions={[PAGE_SIZE]}
        count={events?.totalCount || 0}
        rowsPerPage={PAGE_SIZE}
        page={page}
        onPageChange={onPageChange}
      />
      <Typography variant="caption" color="text.secondary">
        Past subscription and successful payment entries are reconstructed from saved subscription records where possible. Complete renewal, failure, pause, resume, and cancellation events are available from when Razorpay webhook history tracking is enabled.
      </Typography>
    </Box>
  );
}

export default function VMSuperAdminVenueTrack() {
  const { venueId } = useParams();
  const [venue, setVenue] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [venueLoading, setVenueLoading] = useState(true);
  const [sections, setSections] = useState(emptySections);
  const [billing, setBilling] = useState(emptyBilling);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingPages, setBillingPages] = useState({
    subscriptions: 0,
    invoices: 0,
    payments: 0,
  });
  const [selectedBillingView, setSelectedBillingView] = useState(null);
  const [enquiryFilter, setEnquiryFilter] = useState("All");
  const [enquiryPage, setEnquiryPage] = useState(0);
  const [enquiryRows, setEnquiryRows] = useState([]);
  const [enquiryTotal, setEnquiryTotal] = useState(0);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [enquiryCounts, setEnquiryCounts] = useState({
    All: 0,
    Lead: 0,
    Confirmed: 0,
    "Not-interested": 0,
  });

  const loadSection = useCallback(async (section, page) => {
    setSections((prev) => ({
      ...prev,
      [section.key]: { ...prev[section.key], loading: true, page },
    }));
    try {
      const res = await section.fetch({ venue_id: venueId, page, no_of_rows: PAGE_SIZE });
      const { rows, total } = listPayload(res);
      setSections((prev) => ({
        ...prev,
        [section.key]: { rows, total, page, loading: false },
      }));
    } catch (error) {
      setSections((prev) => ({
        ...prev,
        [section.key]: { ...prev[section.key], loading: false, page },
      }));
      toast.error(error?.response?.data?.message || `Could not load ${section.title.toLowerCase()}.`);
    }
  }, [venueId]);

  const loadBilling = useCallback(async (pages) => {
    setBillingLoading(true);
    try {
      const res = await getVenueBilling(venueId, {
        subscriptions_page: pages.subscriptions,
        invoices_page: pages.invoices,
        payments_page: pages.payments,
        invoice_status: selectedBillingView === "outstanding" ? "pending" : "all",
        no_of_rows: PAGE_SIZE,
      });
      if (res.data?.status_code !== 200 || !res.data?.data) {
        throw new Error(res.data?.message || "Could not load billing history.");
      }
      setBilling(res.data.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Could not load billing history.");
    } finally {
      setBillingLoading(false);
    }
  }, [venueId, selectedBillingView]);

  useEffect(() => {
    loadBilling(billingPages);
  }, [billingPages, loadBilling]);

  useEffect(() => {
    const userId = venue?.user_id;
    if (!userId) return undefined;
    let cancelled = false;

    const loadEnquiries = async () => {
      setEnquiryLoading(true);
      try {
        const listUrl = `${API_GET_ENQUIRY_DATA}?user_id=${encodeURIComponent(userId)}&page=${enquiryPage}&no_of_rows=${PAGE_SIZE}&status=${encodeURIComponent(enquiryFilter)}`;
        const [listRes, ...countRes] = await Promise.all([
          axios.get(listUrl),
          ...ENQUIRY_FILTERS.map((status) =>
            axios.get(
              `${API_GET_ENQUIRY_DATA}?user_id=${encodeURIComponent(userId)}&page=0&no_of_rows=1&status=${encodeURIComponent(status)}`
            )
          ),
        ]);
        if (cancelled) return;
        const body = listRes.data || {};
        const rows = Array.isArray(body.data) ? body.data : [];
        setEnquiryRows(rows);
        setEnquiryTotal(Number(body.totalCount || 0));
        setEnquiryCounts(
          Object.fromEntries(
            ENQUIRY_FILTERS.map((status, index) => [
              status,
              Number(countRes[index]?.data?.totalCount || 0),
            ])
          )
        );
      } catch (error) {
        if (!cancelled) {
          setEnquiryRows([]);
          setEnquiryTotal(0);
          toast.error(error?.response?.data?.message || "Could not load enquiries.");
        }
      } finally {
        if (!cancelled) setEnquiryLoading(false);
      }
    };

    loadEnquiries();
    return () => {
      cancelled = true;
    };
  }, [venue?.user_id, enquiryFilter, enquiryPage]);

  useEffect(() => {
    let cancelled = false;
    setVenue(null);
    setUserInfo(null);
    setVenueLoading(true);
    setSections(emptySections());
    setBilling(emptyBilling());
    setBillingPages({ subscriptions: 0, invoices: 0, payments: 0 });

    getVenueById(venueId)
      .then(async (res) => {
        const next = res.data?.data || null;
        if (next?.user_id) {
          try {
            const userRes = await getUserById(next.user_id);
            if (!cancelled) setUserInfo(userRes.data?.data || null);
          } catch (error) {
            if (!cancelled) {
              toast.error(error?.response?.data?.message || "Could not load user account details.");
            }
          }
        }
        if (!cancelled) setVenue(next);
      })
      .catch((error) => {
        if (!cancelled) {
          setVenue(null);
          toast.error(error?.response?.data?.message || "Could not load venue.");
        }
      })
      .finally(() => {
        if (!cancelled) setVenueLoading(false);
      });
    SECTIONS.forEach((section) => {
      loadSection(section, 0);
    });

    return () => {
      cancelled = true;
    };
  }, [venueId, loadSection]);

  const location = [venue?.city_name, venue?.state_name, venue?.country_name].filter(Boolean).join(", ");
  const openBillingView = (key) => {
    setSelectedBillingView(key);
    if (key === "outstanding" || key === "invoices" || key === "invoiced") {
      setBillingPages((current) => ({ ...current, invoices: 0 }));
    }
  };

  return (
    <Box>
      <SuperAdminPageHeader title={venue?.venue_name || "Venue"} />

      {venueLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !venue ? (
        <Typography color="text.secondary">Venue not found.</Typography>
      ) : (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            User
          </Typography>
          <Grid container spacing={2}>
            <Field label="User ID" value={venue.user_id} />
            <Field label="Name" value={userInfo?.name ?? venue.venue_manager_name ?? venue.name} />
            <Field label="Email" value={userInfo?.email ?? venue.manager_email ?? venue.email} />
            <Field
              label="Mobile"
              value={`${userInfo?.country_code || ""} ${userInfo?.mobile_no ?? venue.contact_number ?? venue.mobile_no ?? ""}`.trim()}
            />
            <Field label="Account type" value={userInfo?.user_type} />
            <Field label="Account status" value={userInfo?.status ?? venue.status ?? venue.account_status} />
            <Field label="OTP verified" value={(userInfo?.is_otp_verified ?? venue.is_otp_verified) ? "Yes" : "No"} />
            <Field label="Registered" value={formatDisplayDateTime(userInfo?.created_at ?? venue.created_at)} />
            <Field label="Subscription end date" value={formatDisplayDateTime(userInfo?.subscription_end_date)} />
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                Profile
              </Typography>
              {venue.profile ? <Chip label={venue.profile} size="small" color={venue.profile === "Premium" ? "success" : venue.profile === "Trial" ? "warning" : "default"} /> : <Typography variant="body2">—</Typography>}
            </Grid>
            <Field label="Subscription" value={subscriptionLabel(venue)} />
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2.5, mb: 1.5 }}>
            Venue
          </Typography>
          <Grid container spacing={2}>
            <Field label="Venue ID" value={venue.venue_id} />
            <Field label="Name" value={venue.venue_name} />
            <Field label="Venue type" value={venue.venue_type} />
            <Field label="Status" value={venue.is_venue_active ?? venue.venue_status ? "Active" : "Inactive"} />
            <Field label="Created" value={formatDisplayDateTime(venue.created_at)} />
            <Field label="Location" value={location} />
            <Field label="Address" value={venue.address} />
            <Field label="Pincode" value={venue.pincode} />
            <Field label="Venue mobile" value={venue.venue_mobile_no} />
            <Field label="Venue email" value={venue.venue_email} />
            <Field label="Website" value={venue.website_url} />
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2.5, mb: 1.5 }}>
            Sub-venues ({Array.isArray(venue.sub_venues) ? venue.sub_venues.length : 0})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.main" }}>
                  {["Name", "Type", "Capacity", "Status", "Created"].map((label) => (
                    <TableCell key={label} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!venue.sub_venues?.length ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 2, color: "text.secondary" }}>
                      No sub-venues for this venue.
                    </TableCell>
                  </TableRow>
                ) : (
                  venue.sub_venues.map((sub) => {
                    const active = String(sub.sub_venue_status).toLowerCase() === "active" || sub.sub_venue_status === true;
                    return (
                      <TableRow key={sub.sub_venue_id}>
                        <TableCell>{show(sub.sub_venue_name)}</TableCell>
                        <TableCell>{show(sub.sub_venue_type)}</TableCell>
                        <TableCell>{show(sub.sub_venue_capacity)}</TableCell>
                        <TableCell>
                          <Chip label={active ? "Active" : "Inactive"} size="small" color={active ? "success" : "default"} variant={active ? "filled" : "outlined"} />
                        </TableCell>
                        <TableCell>{formatDisplayDateTime(sub.created_at)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {venue && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
            Billing overview
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Values load when this venue page opens. Select a card to view the related records and how its total is calculated.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            {[
              {
                key: "subscriptions",
                label: "Subscriptions",
                value: billing.subscriptions.totalCount,
              },
              { key: "invoices", label: "Invoices", value: billing.invoice_summary.total },
              {
                key: "invoiced",
                label: "Invoiced",
                value: formatAmount(billing.invoice_summary.invoiced_amount),
              },
              {
                key: "received",
                label: "Received",
                value: formatAmount(billing.invoice_summary.paid_amount),
              },
              {
                key: "outstanding",
                label: "Outstanding",
                value: formatAmount(billing.invoice_summary.pending_amount),
              },
            ].map((card) => (
              <Paper
                key={card.key}
                onClick={() => openBillingView(card.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openBillingView(card.key);
                  }
                }}
                sx={{
                  p: 1.5,
                  textAlign: "center",
                  flex: "1 1 140px",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                <Typography variant="h6">{billingLoading ? "…" : card.value}</Typography>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedBillingView)}
        onClose={() => setSelectedBillingView(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {selectedBillingView === "subscriptions"
            ? "Subscription history"
            : selectedBillingView === "received"
              ? "Received payment history"
              : selectedBillingView === "outstanding"
                ? "Outstanding invoices"
                : selectedBillingView === "invoiced"
                  ? "Invoiced amounts"
                  : "Invoice history"}
          <IconButton onClick={() => setSelectedBillingView(null)} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBillingView && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedBillingView === "subscriptions"
                  ? "Lists subscription records belonging to this venue owner's user account."
                  : selectedBillingView === "received"
                    ? "Lists recorded invoice payments. The received total is the sum of paid amounts on active invoices."
                    : selectedBillingView === "outstanding"
                      ? "Lists unpaid or partially paid invoices. The outstanding total is the sum of their remaining balances."
                      : selectedBillingView === "invoiced"
                        ? "Lists invoices contributing to the invoiced total, which is the sum of payable amounts."
                        : "Lists active invoices associated with this venue owner's account."}
                {" "}Latest rows are shown first; use the table pagination to load more.
              </Typography>
              {selectedBillingView === "subscriptions" ? (
                <SubscriptionHistoryTimeline
                  summary={billing.subscription_summary}
                  events={billing.subscription_timeline}
                  page={billingPages.subscriptions}
                  loading={billingLoading}
                  onPageChange={(_, page) =>
                    setBillingPages((current) => ({ ...current, subscriptions: page }))
                  }
                />
              ) : selectedBillingView === "received" ? (
                <BillingTable
                  title="Payment history"
                  rows={billing.payments.data}
                  count={billing.payments.totalCount}
                  page={billingPages.payments}
                  loading={billingLoading}
                  onPageChange={(_, page) =>
                    setBillingPages((current) => ({ ...current, payments: page }))
                  }
                  columns={[
                    { label: "Payment ID", value: (row) => row.id },
                    { label: "Invoice", value: (row) => row.invoice_id },
                    { label: "Enquiry", value: (row) => row.enquiry_id },
                    { label: "Invoice total", value: (row) => formatAmount(row.total_amount) },
                    { label: "Payment received", value: (row) => formatAmount(row.advance_amount) },
                    { label: "Payment mode", value: (row) => row.payment_mode },
                    { label: "Outstanding", value: (row) => formatAmount(row.pending_amount) },
                    { label: "Date", value: (row) => formatDisplayDateTime(row.created_date) },
                  ]}
                />
              ) : (
                <BillingTable
                  title={selectedBillingView === "outstanding" ? "Outstanding invoices" : "Invoice history"}
                  rows={billing.invoices.data}
                  count={billing.invoices.totalCount}
                  page={billingPages.invoices}
                  loading={billingLoading}
                  onPageChange={(_, page) =>
                    setBillingPages((current) => ({ ...current, invoices: page }))
                  }
                  columns={[
                    { label: "Invoice", value: (row) => row.invoice_id },
                    { label: "Enquiry", value: (row) => row.enquiry_id },
                    { label: "Customer", value: (row) => row.name },
                    { label: "Payable", value: (row) => formatAmount(row.final_payable_amount) },
                    { label: "Paid", value: (row) => formatAmount(row.paid_amount) },
                    { label: "Outstanding", value: (row) => formatAmount(row.pending_amount) },
                    { label: "Payment mode", value: (row) => row.payment_mode },
                    {
                      label: "Status",
                      value: (row) =>
                        String(row.is_invoice_clear).toLowerCase() === "true" ? "Paid" : "Pending",
                    },
                    { label: "Created", value: (row) => formatDisplayDateTime(row.created_date) },
                  ]}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Paper
          sx={{ p: 1.5, textAlign: "center", cursor: "pointer", flex: "1 1 140px" }}
          onClick={() => document.getElementById("venue-track-enquiries")?.scrollIntoView({ behavior: "smooth" })}
        >
          <Typography variant="h6">{enquiryCounts.All}</Typography>
          <Typography variant="caption" color="text.secondary">
            Enquiries
          </Typography>
        </Paper>
        {SECTIONS.map((section) => (
          <Paper
            key={section.key}
            sx={{ p: 1.5, textAlign: "center", cursor: "pointer", flex: "1 1 140px" }}
            onClick={() => document.getElementById(`venue-track-${section.key}`)?.scrollIntoView({ behavior: "smooth" })}
          >
            <Typography variant="h6">{sections[section.key]?.total ?? 0}</Typography>
            <Typography variant="caption" color="text.secondary">
              {section.title}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box id="venue-track-enquiries" sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
          Enquiries
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {ENQUIRY_FILTERS.map((status) => (
            <Chip
              key={status}
              label={`${status} (${enquiryCounts[status] ?? 0})`}
              clickable
              onClick={() => {
                setEnquiryFilter(status);
                setEnquiryPage(0);
              }}
              sx={{
                backgroundColor: updateColorBasedOnStatus(status),
                color: VM_Colors.white,
                fontWeight: status === enquiryFilter ? 700 : 400,
                border: status === enquiryFilter ? "2px solid white" : "none",
                "&:hover": { backgroundColor: updateColorBasedOnStatus(status) },
              }}
            />
          ))}
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {["S.No", "Enquiry ID", "Name", "Phone", "Event Date", "Sub Venue", "Lead Source", "Status", "Created By"].map((label) => (
                  <TableCell key={label} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {enquiryLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : enquiryRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    No enquiries for this venue.
                  </TableCell>
                </TableRow>
              ) : (
                enquiryRows.map((enq, idx) => {
                  const eventDate = Array.isArray(enq.appointment_date)
                    ? enq.appointment_date.map((dateStr) => dayjs(dateStr).format("DD/MM/YYYY")).join(", ")
                    : "";
                  const createdBy = enq.created_by || enq.createdBy || null;
                  return (
                    <TableRow key={enq.appointment_id || idx} hover>
                      <TableCell>{enquiryPage * PAGE_SIZE + idx + 1}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setSelectedEnquiryId(enq.appointment_id)}
                          sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            textTransform: "none",
                            px: 0.5,
                            minWidth: 0,
                          }}
                        >
                          {show(enq.appointment_id)}
                        </Button>
                      </TableCell>
                      <TableCell>{show(enq.name)}</TableCell>
                      <TableCell>{show(`${enq.country_code || ""} ${enq.phone_no || ""}`.trim())}</TableCell>
                      <TableCell>{show(eventDate)}</TableCell>
                      <TableCell>{show(formatSubVenueInfo(enq))}</TableCell>
                      <TableCell>{show(enq.lead)}</TableCell>
                      <TableCell>
                        <StatusButton status={enq.appointment_status} justify="left" />
                      </TableCell>
                      <TableCell>
                        {createdBy && typeof createdBy === "object" ? (
                          <CreatedByCell user={createdBy} />
                        ) : (
                          show(createdBy)
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            rowsPerPageOptions={[PAGE_SIZE]}
            count={enquiryTotal}
            rowsPerPage={PAGE_SIZE}
            page={enquiryPage}
            onPageChange={(_, nextPage) => setEnquiryPage(nextPage)}
          />
        </TableContainer>
      </Box>

      {SECTIONS.map((section) => {
        const state = sections[section.key];
        return (
          <Box key={section.key} id={`venue-track-${section.key}`} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main", mb: 1 }}>
              {section.title}
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "primary.main" }}>
                    {section.columns.map((column) => (
                      <TableCell key={column.label} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.loading ? (
                    <TableRow>
                      <TableCell colSpan={section.columns.length} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : state.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={section.columns.length} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        No {section.title.toLowerCase()} for this venue.
                      </TableCell>
                    </TableRow>
                  ) : (
                    state.rows.map((row, idx) => (
                      <TableRow key={`${section.key}-${row.id ?? row.enquiry_id ?? idx}`} hover>
                        {section.columns.map((column) => (
                          <TableCell key={column.label}>{show(column.value(row))}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                rowsPerPageOptions={[PAGE_SIZE]}
                count={state.total}
                rowsPerPage={PAGE_SIZE}
                page={state.page}
                onPageChange={(_, nextPage) => loadSection(section, nextPage)}
              />
            </TableContainer>
          </Box>
        );
      })}

      <Dialog
        open={Boolean(selectedEnquiryId)}
        onClose={() => setSelectedEnquiryId(null)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          Enquiry {selectedEnquiryId}
          <IconButton onClick={() => setSelectedEnquiryId(null)} aria-label="Close" size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEnquiryId ? <SuperAdminEnquiryDetails enquiryId={selectedEnquiryId} /> : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
